import type { HeartsMove, HeartsView } from "@bing/engine-hearts";
import type { LobbyPlayer } from "./online-connection";
import type { ReactNode } from "react";
import { fa } from "../i18n/fa";
import { HeartsTable, type HeartsSeatPlayer } from "../screens/HeartsTable";
import { useSession } from "../store/context";
import { WinOverlay } from "./HeartsGame";
import { OnlineGame } from "./OnlineGame";

/** The lobby roster as the felt and the result card both want it. ONE mapping, so a chair can
 *  never be a named person on the table and a nameless «بات» on the end screen. */
function seatPlayers(
  players: readonly LobbyPlayer[] | undefined,
): readonly HeartsSeatPlayer[] | undefined {
  return players?.map((p) => ({
    seat: p.seat,
    isBot: p.isBot,
    nickname: p.name,
    // Same as Hokm: carry the seat's own avatar through to the felt.
    avatarId: p.user?.avatarId,
    avatarPhotoId: p.user?.avatarPhotoId,
    isOfficial: p.user?.isOfficial,
  }));
}

/** Online بی‌دل — thin wrapper over the shared `OnlineGame` shell (quick match,
 * private tables, host controls, turn timer + pause all live in the shell).
 * Threads the self profile (own-seat photo + name), the server-authoritative
 * `legal` list, and the lobby roster (real-user seat names, mapped onto
 * `displayName()`'s `nickname` slot) — exactly like HokmOnlineGame. */
export function HeartsOnlineGame(): ReactNode {
  const profile = useSession((s) => s.profile);
  return (
    <OnlineGame<HeartsMove, HeartsView>
      gameId="hearts"
      room="hearts-online"
      title={fa.hearts.title}
      renderTable={({ view, legal, send, leave, players, timer, onInviteSeat, seatNotices }) => (
        <HeartsTable
          seatNotices={seatNotices}
          view={view}
          legal={legal}
          onMove={send}
          onLeave={leave}
          self={profile}
          timer={timer}
          onInviteSeat={onInviteSeat}
          players={seatPlayers(players)}
        />
      )}
      renderWin={({
        result,
        view,
        again,
        leave,
        players,
        againDisabled,
        againHint,
        againHidden,
      }) => (
        <WinOverlay
          result={result}
          totalScores={view.totalScores}
          you={view.you}
          // The SAME identity pair the felt gets — without them the result card called every
          // real opponent «ربات N» (prod report 2026-08-29).
          self={profile}
          players={seatPlayers(players)}
          onAgain={again}
          againDisabled={againDisabled}
          againHint={againHint}
          againHidden={againHidden}
          onLeave={leave}
        />
      )}
    />
  );
}
